<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CalculationMode } from '@fire-calc/domain-ts';

interface Props {
  classicalValue: string;
  classicalSuffix?: string;
  dwzValue: string;
  dwzSuffix?: string;
  prose?: string | null;
  mode: CalculationMode;
}

const props = withDefaults(defineProps<Props>(), {
  classicalSuffix: '',
  dwzSuffix: '',
  prose: null,
});

const emit = defineEmits<{ tap: [] }>();

const { t } = useI18n();

const unitLabel = computed(() =>
  props.mode === CalculationMode.byRetirementAge
    ? 'Kč'
    : t('heroUnitYears'),
);
</script>

<template>
  <div data-testid="floating-bar" class="fixed left-0 right-0 bottom-0 z-50 flex justify-center pointer-events-none">
    <button
      type="button"
      class="pointer-events-auto mx-auto w-full max-w-floating-bar bg-bg-card rounded-large shadow-floating-bar px-lg py-md flex flex-col items-stretch gap-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-page transition-colors duration-fast hover:bg-grey-200 active:bg-grey-300"
      @click="emit('tap')"
    >
      <div class="flex items-stretch gap-md">
        <div class="flex-1 flex flex-col items-end">
          <span class="text-label-small font-bold uppercase text-text-tertiary tracking-wide">{{ t('strategyClassical').toUpperCase() }}</span>
          <span class="flex items-baseline gap-1 mt-xs whitespace-nowrap">
            <span class="text-headline-medium font-bold text-trad-500 leading-none whitespace-nowrap">{{ classicalValue }}</span>
            <span v-if="classicalSuffix" class="text-title-large font-medium text-trad-500 leading-none whitespace-nowrap">{{ classicalSuffix }}</span>
            <span class="text-label-small font-bold text-trad-500 whitespace-nowrap">{{ unitLabel }}</span>
          </span>
        </div>
        <div class="flex-1 flex flex-col items-start">
          <span class="text-label-small font-bold uppercase text-text-tertiary tracking-wide">{{ t('strategyDwz').toUpperCase() }}</span>
          <span class="flex items-baseline gap-1 mt-xs whitespace-nowrap">
            <span class="text-headline-medium font-bold text-dwz-500 leading-none whitespace-nowrap">{{ dwzValue }}</span>
            <span v-if="dwzSuffix" class="text-title-large font-medium text-dwz-500 leading-none whitespace-nowrap">{{ dwzSuffix }}</span>
            <span class="text-label-small font-bold text-dwz-500 whitespace-nowrap">{{ unitLabel }}</span>
          </span>
        </div>
      </div>
      <p v-if="prose" class="text-body-small text-text-secondary text-center">{{ prose }}</p>
      <div class="flex justify-center">
        <div class="w-12 h-1.5 rounded-pill bg-brand-300" />
      </div>
    </button>
  </div>
</template>
